import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useLocation } from "react-router-dom"

import { Loading, SectionHeading } from "../../components/common"
import { FooterButtonVolver } from "../../components/buttons/FooterButtonVolver/FooterButtonVolver"
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, dataTaggerActionClear, dataTaggerActionSet } from '@laramie/ui';
import Definicion from "./components/Definicion"
import Parametros from "./components/Parametros"
import Variables from "./components/Variables"
import { ServerRequest } from "../../utils/apiweb"
import { APIS } from "../../config/apis"
import { initialFormValues, initialOtherValues } from './utils'

import InfoAdicional from "./components/InfoAdicional"
import { getDateId } from "../../utils/convert"
import Filtros from "./components/Filtros"

import { useForm, useFormErrors, useNav, useUnsavedChangesGuard } from "../../components/hooks"
import { VALIDATION_MESSAGE } from "../../consts/validationMessage"

const ProcedimientoView = () => {
    const params = useParams()
    const location = useLocation()
    const navigate = useNav()
    const dispatch = useDispatch()
    const filters = location.state?.filters
    const dataTagger = useSelector(state => state.dataTagger.data)
    const [pendingChange, setPendingChange] = useState(false);

    const [loading, setLoading] = useState(false)
    const [formValues, handleInput, , setFormValues] = useForm(initialFormValues)
    const [otherValues, setOtherValues] = useState(initialOtherValues)
    const [idUsuarioCreacion, setIdUsuarioCreacion] = useState(null)
    const [colecciones, setColecciones] = useState([])
    const [coleccionesCampo, setColeccionesCampo] = useState([])
    const [processKey] = useState(`Procedimiento${params.id??0}_${getDateId()}`)

    const {allowNavigation} = useUnsavedChangesGuard(pendingChange);

    const setStateFromData = data => {
        if (data.procedimiento.fechaCreacion) data.procedimiento.fechaCreacion = new Date(data.procedimiento.fechaCreacion);
        setFormValues(data.procedimiento)
        setOtherValues({
            ...data,
            procedimiento: undefined,
        })
        setIdUsuarioCreacion(data.procedimiento.idUsuarioCreacion);
        dispatch(dataTaggerActionSet(processKey, {
            Archivo: data.archivos,
            Observacion: data.observaciones,
            Etiqueta: data.etiquetas
        }))
    }

    const onNoSuccess = (response) => {
        response.json()
            .then((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                setLoading(false)
            })
            .catch((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
                setLoading(false)
            });
    }

    const onError = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message)
        setLoading(false)
    }

    useEffect(() => {
        if (params.mode === OPERATION_MODE.NEW)
            return

        setLoading(true)

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.PROCEDIMIENTO,
            `/full/${params.id}`,
            null,
            res => res.json().then(data => {
                setStateFromData(data)
                setLoading(false)
            }),
            onNoSuccess,
            onError,
        )
    }, [])

    useEffect(() => {
        if (formValues.idTipoTributo < 1 || params.mode === OPERATION_MODE.NEW)
            return

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.COLECCION,
            `/tipo-tributo/${formValues.idTipoTributo}`,
            null,
            res => res.json().then(data => {
                setColecciones(data)
                setColeccionesCampo(data.reduce((prev, curr) => [...prev, ...curr.coleccionesCampo], []))
            }),
            onNoSuccess,
            onError,
        )
    }, [formValues.idTipoTributo])

    const handleInputProxy = (event) => {
        handleInput(event);
        setPendingChange(true);
    }   

    const [definicionOpen, setDefinicionOpen] = useState(true)

    const { errorOf, validate } = useFormErrors(getErrors)

    function getErrors() {
        const errors = {}

        if (formValues.nombre.length < 1) errors.nombre = VALIDATION_MESSAGE.REQUIRED
        if (formValues.descripcion.length < 1) errors.descripcion = VALIDATION_MESSAGE.REQUIRED
        if (formValues.idTipoTributo <= 0) errors.idTipoTributo = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (formValues.idEstadoProcedimiento <= 0) errors.idEstadoProcedimiento = VALIDATION_MESSAGE.REQUIRED_OPTION

        return errors
    }

    const saveChanges = () => {
        if (!validate()) {
            setDefinicionOpen(true)
            return
        }

        setLoading(true)

        const stateFilter = x => x.state !== 'o' && x.state !== undefined
        const dataBody = {
            procedimiento: formValues,
            procedimientoFiltros: otherValues.procedimientoFiltros.filter(stateFilter),
            procedimientoParametros: otherValues.procedimientoParametros.filter(stateFilter),
            procedimientoVariables: otherValues.procedimientoVariables.filter(stateFilter),
            archivos: (dataTagger[processKey]) ? dataTagger[processKey].Archivo.filter(stateFilter) : [],
            observaciones: (dataTagger[processKey]) ? dataTagger[processKey].Observacion.filter(stateFilter) : [],
            etiquetas: (dataTagger[processKey]) ? dataTagger[processKey].Etiqueta.filter(stateFilter) : []
        }

        if (params.mode === OPERATION_MODE.NEW) {
            ServerRequest(
                REQUEST_METHOD.POST,
                null,
                true,
                APIS.URLS.PROCEDIMIENTO,
                null,
                dataBody,
                res => res.json()
                    .then(data => {
                        setPendingChange(false);
                        ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Procedimiento grabado correctamente', () => {
                            dispatch(dataTaggerActionClear(processKey))
                            setLoading(false)
                            const url = `/procedimiento/${OPERATION_MODE.EDIT}/${data.procedimiento.id}`;
                            allowNavigation()
                            navigate({
                                to: `/procedimiento/edit/${data.procedimiento.id}`,
                                refresh: true,
                                replace: true,
                            })
                        });
                    })
                    .catch((error) => {
                        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error)
                        setLoading(false)
                    }),
                onNoSuccess,
                onError,
            )
        }

        if (params.mode === OPERATION_MODE.EDIT) {
            ServerRequest(
                REQUEST_METHOD.PUT,
                null,
                true,
                APIS.URLS.PROCEDIMIENTO,
                `/${formValues.id}`,
                dataBody,
                res => res.json().then(data => {
                    setPendingChange(false);
                    setStateFromData(data)
                    setLoading(false)
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Procedimiento grabado correctamente')
                }),
                onNoSuccess,
                onError,
            )
        }
    }

    return (
        <div>
            <Loading visible={loading}></Loading>
            <SectionHeading
                titles={[
                    { title: 'Emisión' },
                    { title: 'Procedimientos de emisión', url: '/procedimientos' },
                    { title: params.mode === OPERATION_MODE.NEW ? 'Nuevo' : `Nombre: ${formValues.nombre}`}
                ]}
            />

            <div className="m-top-20">
                <section className='section-accordion'>
                    <div className="m-top-20 m-bottom-20"> 
                        <Definicion
                            {...{formValues, handleInputProxy, errorOf, otherValues, params, idUsuarioCreacion}}
                            open={definicionOpen}
                            onToggle={setDefinicionOpen}
                        />

                        {params.mode !== OPERATION_MODE.NEW && <>
                            <Filtros
                                idTipoTributo={formValues.idTipoTributo}
                                params={params}
                                data={otherValues.procedimientoFiltros}
                                setData={data => setOtherValues(prev => ({...prev, procedimientoFiltros: data}))}
                                setPendingChange={setPendingChange}
                            />
                            <Parametros
                                params={params}
                                data={otherValues.procedimientoParametros}
                                setData={data => setOtherValues(prev => ({...prev, procedimientoParametros: data}))}
                                setPendingChange={setPendingChange}
                            />

                            <Variables
                                {...{params, colecciones, coleccionesCampo}}
                                data={otherValues.procedimientoVariables}
                                setData={data => setOtherValues(prev => ({...prev, procedimientoVariables: data}))}
                                setPendingChange={setPendingChange}
                            />

                            <InfoAdicional
                                title="Información adicional de procedimiento"
                                entity="Procedimiento"
                                idEntity={parseInt(params.id)}
                                mode={params.mode}
                                processKey={processKey}
                                setPendingChange={setPendingChange}
                            />
                        </>}
                    </div>
                </section>
            </div>

            <footer className='footer footer-action'>
                <div className='footer-action-container'>
                    <FooterButtonVolver
                        filters={filters}
                        to="/procedimientos"
                        pendingChange={pendingChange}
                        processKey={processKey}
                        allowNavigation={allowNavigation}
                    />
                    {params.mode !== OPERATION_MODE.VIEW && (
                        <button
                            className={pendingChange ? "btn action-button float-end" : "btn action-button btn-disabled float-end"}
                            onClick={saveChanges}
                            disabled={!pendingChange}
                        >
                            {params.mode === OPERATION_MODE.NEW ? 'Siguiente' : 'Guardar'}
                        </button>
                    )}
                </div>
            </footer>
        </div>
    )
}

export default ProcedimientoView
